import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import CheckoutForm from '../../components/CheckoutForm';
import { useHeaderContext } from '../../context/headerContext';
import { useUserContext } from '../../context/userContext';
import { useLocation } from 'react-router-dom';
import fetchWithRefresh from '../../utils/fetchWithRefresh';
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";

const initStripe = async () => {
    const token = localStorage.getItem('auth_session');

    try {
        return await Sentry.startSpan({ name: "Init Stripe", op: "http.request" }, async (span) => {
            const url = `${import.meta.env.VITE_API_URL}Stripe/FetchPublishableKey`;
            span.setAttribute("http.url", url);
            span.setAttribute("userTokenPresent", !!token);

            const response = await fetch(url, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                const publishableKey = data.PublishableKey;
                span.setStatus("ok");
                return loadStripe(publishableKey);
            } else {
                const errorMessage = `Failed to fetch Stripe publishable key. Status: ${response.status}`;
                span.setStatus("error");
                Sentry.captureMessage(errorMessage, "warning");
                // LogErrors(user, logout, updateTokens, errorMessage);
                return;
            }
        });
    } catch (error) {
        Sentry.captureException(error, { extra: { tokenPresent: !!token } });
        // LogErrors(user, logout, updateTokens, "Error initializing Stripe: " + error);
    }
};

const stripeCheckoutStyles = {
    variables: {
        fontFamily: 'Poppins, sans-serif',
        fontSizeSm: '16px',
        fontLineHeight: '26px',
        colorPrimaryText: '#101820',
        colorTextSecondary: '#101820',
        borderRadius: '4px',
        borderWidth: '1px',
        colorBackground: '#F8F8F8',
        colorComponentBorder: '#E3E3E3',
        colorComponentText: '#101820',
        colorTextPlaceholder: '#727272',
        colorDanger: '#DF3327',
        colorWarning: '#DF3327',
        colorSuccess: '#47B405',
        spacingGridRow: '24px',
        gridColumnSpacing: '24px',
        iconCardCvcColor: '#6D6E77',
        iconChevronDownColor: '#000000',
        iconChevronDownHoverColor: '#000000',
    },
};

export default function Checkout() {
    const { setTitle, setBackButton } = useHeaderContext();
    const { user, logout, updateTokens } = useUserContext();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const amount = queryParams.get('amount') || "";
    const credits = queryParams.get('product') || "";
    const productId = queryParams.get('id') || "";

    useEffect(() => {
        setTitle("Checkout");
        setBackButton(true);
    }, []);

    const stripePromise = initStripe();
    const [clientSecretSettings, setClientSecretSettings] = useState({
      clientSecret: "",
      loading: true,
    });
  
    useEffect(() => {
        async function createPaymentIntent() {
          try {
            return await Sentry.startSpan({ name: "Fetch Payment Intent", op: "http.request" }, async (span) => {
              const url = `${import.meta.env.VITE_API_URL}Stripe/FetchPaymentIntent/?amount=${amount}&productId=${productId}&userAccountId=${user?.id}`;
              span.setAttribute("http.url", url);
              span.setAttribute("userAccountId", user?.id);
      
              const response = await fetchWithRefresh(url, {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${user.token}`,
                },
              }, logout, updateTokens, (message) => { toast.info(message); });
      
              if (response.ok) {
                const data = await response.json();
                // LogErrors(user, logout, updateTokens, data);
      
                setClientSecretSettings({
                  clientSecret: data.PaymentIntentClientSecret,
                  loading: false,
                });
      
                span.setStatus("ok");
              } else {
                const errorMessage = `Failed to fetch payment intent. Status: ${response.status}`;
                span.setStatus("error");
                Sentry.captureMessage(errorMessage, "warning");
                LogErrors(user, logout, updateTokens, errorMessage);
              }
            });
          } catch (error) {
            Sentry.captureException(error, { extra: { userAccountId: user?.id } });
            LogErrors(user, logout, updateTokens, "Error fetching payment intent: " + error);
          }
        }
      
        createPaymentIntent();
      }, []);
  
    return (
        <>
            {clientSecretSettings.loading ? (
                <h1>Loading ...</h1>
            ) : (
                <div>
                    <div className='flex flex-col gap-1 pb-6 border-dashed border-b border-[#E3E3E3]'>
                        <p className="text-[12px] color-[#101820] leading-6 tracking-[2px] uppercase font-semibold">{credits}</p>
                        <h2>£{(amount/100).toFixed(2)}</h2>
                    </div>

                    <Elements
                        stripe={stripePromise}
                        options={{
                            clientSecret: clientSecretSettings.clientSecret,
                            appearance: stripeCheckoutStyles,
                        }}
                    >
                        <CheckoutForm amount={(amount/100).toFixed(2)} />
                    </Elements>
                </div>
            )}
        </>
    );
};